import { defineStore } from 'pinia';
import { ref } from 'vue';
import type {
  IApiResponseCategory,
  ICategoryBody,
  IApiResponseCategoryById,
} from '@/types/category.types';
import { CategoryService } from '@/services/category.services';

/**
 * Store pour gérer les catégories et leurs actions associées
 */
export const useCategoryStore = defineStore('category', () => {
  /**
   * État réactif contenant la liste des catégories récupérées depuis l’API.
   *
   * Ce ref stocke la réponse typée de l’API après l’appel à `getCategory`.
   * Il est mis à jour automatiquement lorsque les données sont chargées
   * depuis le service distant.
   *
   * @type {Ref<IApiResponseCategory | null>}
   */
  const datagetCategory = ref<IApiResponseCategory | null>(null);

  /**
   * Récupère la liste des catégories depuis le service distant.
   *
   * Cette fonction effectue une requête HTTP via le `CategoryService`
   * pour récupérer les catégories disponibles, puis met à jour
   * l’état réactif `datagetCategory` avec les données renvoyées.
   *
   * @async
   * @function getCategory
   * @returns {Promise<IApiResponseCategory>} Promesse résolue contenant les données des catégories récupérées.
   * @throws {Error} Lance une erreur si la récupération échoue.
   */
  const getCategory = async (): Promise<IApiResponseCategory> => {
    const response = await CategoryService.getCategory();
    datagetCategory.value = response.data;
    return response.data;
  };

  /**
   * Ce `ref` stocke la réponse typée de l’API après l’appel à `getCategoryById`.
   * Il est mis à jour automatiquement lorsque les données sont chargées
   * depuis le service distant.
   *
   * @type {Ref<IApiResponseCategoryById | null>}
   */
  const dataGetCategoryById = ref<IApiResponseCategoryById | null>(null);

  /**
   * Récupère une catégorie spécifique depuis le service distant.
   *
   * Cette fonction effectue une requête HTTP via `CategoryService`
   * pour obtenir les informations de la catégorie, puis met à jour
   * l’état réactif `dataGetCategoryById` avec les données reçues.
   *
   * @async
   * @function getCategoryById
   * @returns {Promise<IApiResponseCategoryById>} Promesse résolue contenant les données de la catégorie récupérée.
   * @throws {Error} Lance une erreur si la récupération échoue.
   */
  const getCategoryById = async (): Promise<IApiResponseCategoryById> => {
    const response = await CategoryService.getCategoryById();
    dataGetCategoryById.value = response.data;
    return response.data;
  };

  /**
   * Crée une nouvelle catégorie via le service distant.
   *
   * Cette fonction envoie les données d'une catégorie au backend pour
   * l'enregistrement dans la base de données.
   * Elle peut être utilisée lors de la création ou de la mise à jour
   * d'une catégorie selon le contexte.
   *
   * @async
   * @function createCategory
   * @param {ICategoryBody} data - Données de la catégorie à créer.
   * @returns {Promise<IApiResponseCategory>} - Réponse complète de l'API contenant les informations de la catégorie créée.
   * @throws {Error} Lance une erreur si la requête échoue ou si le service renvoie une erreur.
   */
  const createCategory = async (data: ICategoryBody): Promise<any> => {
    try {
      const response = await CategoryService.createCategory(data);
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  /**
   * Supprime une catégorie de produits via le service distant.
   *
   * Cette fonction appelle le `CategoryService` pour supprimer
   * une catégorie existante, et renvoie la réponse de l'API.
   *
   * @async
   * @function DeleteCategorie
   * @returns {Promise<any>} Promesse résolue avec la réponse de l'API après suppression.
   * @throws {Error} Lance une erreur si la suppression échoue.
   */
  const DeleteCategorie = async (): Promise<any> => {
    try {
      console.log('hereeeeeeeeee');
      const response = await CategoryService.DeleteCategory();
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  /**
   * Met à jour une catégorie spécifique sur le service distant.
   *
   * Cette fonction effectue une requête HTTP via `CategoryService`
   * pour modifier les informations d’une catégorie existante,
   * puis renvoie les données mises à jour.
   *
   * @async
   * @function updateCategoryById
   * @param {Partial<ICategoryBody>} categoryData - Les données de la catégorie à mettre à jour.
   * @returns {Promise<ICategoryBody>} Promesse résolue contenant les données mises à jour de la catégorie.
   * @throws {Error} Lance une erreur si la mise à jour échoue.
   */
  const updateCategoryById = async (
    categoryData: Partial<ICategoryBody>,
  ): Promise<ICategoryBody> => {
    const response = await CategoryService.updateCategoryById(categoryData);
    return response.data;
  };

  return {
    createCategory,
    getCategory,
    datagetCategory,
    getCategoryById,
    dataGetCategoryById,
    updateCategoryById,
    DeleteCategorie,
  };
});
